<template>
  <div class="projects-view">
    <div class="page-header">
      <h1 class="page-title">{{ t('projects.title') }}</h1>
      <button class="btn btn-primary" @click="openForm(null)">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
        {{ t('projects.newProject') }}
      </button>
    </div>

    <div class="proj-filters">
      <span class="proj-filter-label">{{ t('common.from') }}</span>
      <input v-model="dateFrom" type="date" class="form-control filter-date" @change="load()" />
      <span class="proj-filter-label">{{ t('common.to') }}</span>
      <input v-model="dateTo" type="date" class="form-control filter-date" @change="load()" />
      <button v-if="dateFrom || dateTo" class="btn btn-ghost btn-sm" @click="resetFilters">{{ t('common.reset') }}</button>
    </div>

    <div v-if="loading" class="spinner"></div>

    <div v-else-if="projects.length === 0" class="empty-state">
      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" opacity=".3"><path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z"/></svg>
      <p>{{ t('projects.empty') }}</p>
    </div>

    <p v-else class="drag-hint">{{ t('projects.dragHint') }}</p>
    <draggable v-if="!loading && projects.length" v-model="projects" item-key="id" handle=".proj-grip" class="proj-list" @end="onProjectReorder">
      <template #item="{ element: p }">
        <div class="project-card">
          <div class="project-color" :style="{ background: p.color }"></div>
          <div class="project-body">
            <div class="project-head">
              <span class="proj-grip" :title="t('projects.dragHint')">⠿</span>
              <button class="proj-toggle" @click="toggle(p)" :aria-expanded="!!expanded[p.id]">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" :class="{ open: expanded[p.id] }"><polyline points="9 18 15 12 9 6"/></svg>
              </button>
              <h3 class="project-name" @click="toggle(p)">{{ p.name }}</h3>
              <div class="project-actions">
                <router-link :to="tasksLink(p)" class="btn btn-sm pa-view">{{ t('projects.viewTasks') }}</router-link>
                <button class="btn btn-sm btn-icon pa-icon" :title="t('common.edit')" @click="openForm(p)">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                </button>
                <button class="btn btn-sm btn-icon pa-icon danger-icon" :title="t('projects.archiveTitle')" @click="archiveProject(p)">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="21 8 21 21 3 21 3 8"/><rect x="1" y="3" width="22" height="5"/><line x1="10" y1="12" x2="14" y2="12"/></svg>
                </button>
              </div>
              <span class="vis-badge" :class="p.visibility === 'private' ? 'vis-private' : 'vis-shared'">
                {{ p.visibility === 'private' ? t('projects.privateBadge') : t('projects.sharedBadge') }}
              </span>
              <span class="spacer"></span>
              <span class="task-count">{{ p.task_count }} {{ p.task_count === 1 ? t('projects.taskCountOne') : t('projects.taskCountMany') }}</span>
            </div>
            <p v-if="p.description" class="project-desc">{{ p.description }}</p>

            <!-- Collapsible task list (draggable, droppable across projects) -->
            <div v-if="expanded[p.id]" class="ptasks">
              <div v-if="loadingTasks[p.id]" class="ptask-empty">…</div>
              <template v-else>
                <draggable :list="projectTasks[p.id]" item-key="id" group="ptasks" class="ptask-list" @change="e => onTaskChange(e, p.id)">
                  <template #item="{ element: tk }">
                    <div class="ptask">
                      <span class="ptask-grip">⠿</span>
                      <span class="ptask-title" :class="{ done: tk.status === 'done' }">{{ tk.title }}</span>
                      <span :class="['badge', 'badge-' + tk.status]">{{ t('status.' + tk.status) }}</span>
                    </div>
                  </template>
                </draggable>
                <div v-if="(projectTasks[p.id] || []).length === 0" class="ptask-empty">{{ t('projects.noTasks') }}</div>
              </template>
            </div>
          </div>
        </div>
      </template>
    </draggable>

    <!-- Project form modal -->
    <div v-if="showForm" class="modal-backdrop" @click.self="showForm = false">
      <div class="modal">
        <div class="modal-header">
          <h2>{{ editing ? t('projects.editProject') : t('projects.newProject') }}</h2>
          <button class="btn btn-icon modal-close" @click="showForm = false">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
        <form @submit.prevent="submitProject">
          <div class="form-group">
            <label>{{ t('projects.name') }} *</label>
            <input v-model="pForm.name" class="form-control" required maxlength="100" />
          </div>
          <div class="form-group">
            <label>{{ t('projects.description') }}</label>
            <textarea v-model="pForm.description" class="form-control" rows="2"></textarea>
          </div>
          <div class="form-row">
            <div class="form-group">
              <label>{{ t('projects.color') }}</label>
              <div class="color-picker">
                <input type="color" v-model="pForm.color" class="color-input" />
                <span class="color-preview" :style="{ background: pForm.color }"></span>
                <input v-model="pForm.color" class="form-control" style="font-size:12px" placeholder="#76B900" />
              </div>
            </div>
            <div class="form-group">
              <label>{{ t('projects.visibility') }}</label>
              <select v-model="pForm.visibility" class="form-control">
                <option value="shared">{{ t('projects.shared') }}</option>
                <option value="private">{{ t('projects.private') }}</option>
              </select>
            </div>
          </div>
          <!-- Pricing: fixed up-front total or per-hour rate -->
          <div class="form-group pricing-block">
            <label>{{ t('projects.pricing') }}</label>
            <select v-model="pForm.pricing_type" class="form-control">
              <option value="none">{{ t('projects.pricingNone') }}</option>
              <option value="fixed">{{ t('projects.pricingFixed') }}</option>
              <option value="hourly">{{ t('projects.pricingHourly') }}</option>
            </select>
            <div v-if="pForm.pricing_type !== 'none'" class="pricing-amount">
              <input
                v-model="pForm.price_amount" type="number" min="0" step="0.01" class="form-control"
                :placeholder="pForm.pricing_type === 'hourly' ? t('projects.ratePlaceholder') : t('projects.totalPlaceholder')"
              />
              <input v-model="pForm.currency" class="form-control currency-input" maxlength="8" placeholder="ILS" />
            </div>
          </div>
          <!-- Members: only for private projects the current user manages -->
          <div v-if="showMembers" class="form-group members-block">
            <label>{{ t('projects.members') }}</label>
            <p class="members-hint">{{ t('projects.membersHint') }}</p>
            <template v-if="editing">
              <div class="members-list">
                <span v-for="m in members" :key="m.id" class="member-chip">
                  {{ m.name }}
                  <button type="button" class="member-remove" :title="t('projects.remove')" @click="removeMember(m.id)">×</button>
                </span>
                <span v-if="members.length === 0" class="members-empty">{{ t('projects.noMembers') }}</span>
              </div>
              <div class="member-add">
                <select v-model="memberToAdd" class="form-control">
                  <option value="">{{ t('projects.selectUser') }}</option>
                  <option v-for="u in addableUsers" :key="u.id" :value="u.id">{{ u.name }}</option>
                </select>
                <button type="button" class="btn btn-ghost btn-sm" :disabled="!memberToAdd" @click="addMember">
                  {{ t('projects.addMember') }}
                </button>
              </div>
            </template>
            <p v-else class="members-empty">{{ t('projects.saveFirst') }}</p>
          </div>
          <!-- Clients this project is built for (read access to its tasks + hours) -->
          <div v-if="canManage" class="form-group members-block">
            <label>{{ t('projects.clients') }}</label>
            <p class="members-hint">{{ t('projects.clientsHint') }}</p>
            <template v-if="editing">
              <div class="members-list">
                <span v-for="c in projectClients" :key="c.id" class="member-chip">
                  {{ c.name }}
                  <button type="button" class="member-remove" :title="t('projects.remove')" @click="removeClient(c.id)">×</button>
                </span>
                <span v-if="projectClients.length === 0" class="members-empty">{{ t('projects.noClients') }}</span>
              </div>
              <div class="member-add">
                <select v-model="clientToAdd" class="form-control">
                  <option value="">{{ t('projects.selectClient') }}</option>
                  <option v-for="c in addableClients" :key="c.id" :value="c.id">{{ c.name }}</option>
                </select>
                <button type="button" class="btn btn-ghost btn-sm" :disabled="!clientToAdd" @click="addClient">
                  {{ t('projects.addClientBtn') }}
                </button>
              </div>
            </template>
            <p v-else class="members-empty">{{ t('projects.saveFirst') }}</p>
          </div>

          <p v-if="pError" class="form-error">{{ pError }}</p>
          <div class="modal-actions">
            <button type="button" class="btn btn-ghost" @click="showForm = false">{{ t('common.cancel') }}</button>
            <button type="submit" class="btn btn-primary" :disabled="pSaving">
              {{ pSaving ? t('common.saving') : (editing ? t('common.save') : t('common.create')) }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted, onUnmounted } from 'vue'
import draggable from 'vuedraggable'
import api from '@/api'
import { useI18n } from '@/composables/useI18n'
import { useAuth } from '@/composables/useAuth'
import { onRealtime, debounce } from '@/composables/useRealtime'

export default {
  name: 'ProjectsView',
  components: { draggable },
  setup() {
    const { t }    = useI18n()
    const { currentUser, isManager } = useAuth()
    const projects = ref([])
    const loading  = ref(true)
    const showForm = ref(false)
    const editing  = ref(null)
    const pSaving  = ref(false)
    const pError   = ref('')

    const pForm = reactive({
      name: '', description: '', color: '#76B900', visibility: 'shared',
      pricing_type: 'none', price_amount: '', currency: 'ILS',
    })

    // Creation-date range filter
    const dateFrom = ref('')
    const dateTo   = ref('')
    function resetFilters() { dateFrom.value = ''; dateTo.value = ''; load() }

    // ── Project members (private projects only) ──
    const members     = ref([])
    const allUsers    = ref([])
    const memberToAdd = ref('')

    const canManage = computed(() =>
      isManager() || (editing.value && editing.value.created_by === currentUser()?.id)
    )
    const showMembers = computed(() => pForm.visibility === 'private' && canManage.value)
    const addableUsers = computed(() => {
      const taken = new Set(members.value.map(m => m.id))
      const creatorId = editing.value?.created_by
      return allUsers.value.filter(u => !taken.has(u.id) && u.id !== creatorId)
    })

    async function loadMembers() {
      if (!editing.value) { members.value = []; return }
      try { members.value = (await api.getProjectMembers(editing.value.id)).data || [] } catch {}
    }
    async function loadUsers() {
      if (allUsers.value.length) return
      try { allUsers.value = (await api.getUsers()).data || [] } catch {}
    }
    async function addMember() {
      if (!memberToAdd.value || !editing.value) return
      try {
        await api.addProjectMember(editing.value.id, memberToAdd.value)
        memberToAdd.value = ''
        await loadMembers()
        await load()
      } catch (e) { pError.value = e.message || t('taskForm.saveFailed') }
    }
    async function removeMember(uid) {
      if (!editing.value) return
      try { await api.removeProjectMember(editing.value.id, uid); await loadMembers(); await load() } catch {}
    }

    // ── Clients assigned to the project ──
    const projectClients = ref([])
    const allClients     = ref([])
    const clientToAdd    = ref('')
    const addableClients = computed(() => {
      const taken = new Set(projectClients.value.map(c => c.id))
      return allClients.value.filter(c => !taken.has(c.id))
    })
    async function loadProjectClients() {
      if (!editing.value) { projectClients.value = []; return }
      try { projectClients.value = (await api.getProjectClients(editing.value.id)).data || [] } catch {}
    }
    async function loadAllClients() {
      if (allClients.value.length) return
      try { allClients.value = (await api.getClients()).data || [] } catch {}
    }
    async function addClient() {
      if (!clientToAdd.value || !editing.value) return
      try {
        await api.addProjectClient(editing.value.id, clientToAdd.value)
        clientToAdd.value = ''
        await loadProjectClients()
        await load()
      } catch (e) { pError.value = e.message || t('taskForm.saveFailed') }
    }
    async function removeClient(cid) {
      if (!editing.value) return
      try { await api.removeProjectClient(editing.value.id, cid); await loadProjectClients(); await load() } catch {}
    }

    async function load() {
      loading.value = true
      try {
        const params = {
          ...(dateFrom.value ? { created_from: dateFrom.value } : {}),
          ...(dateTo.value   ? { created_to: dateTo.value }     : {}),
        }
        projects.value = (await api.getProjects(params)).data || []
      } catch {} finally { loading.value = false }
    }

    // Viewing a project shows all of its tasks (access is governed by project
    // membership on the backend), so we never force the "mine" filter here.
    function tasksLink(p) {
      return `/tasks?project_id=${p.id}`
    }

    // ── Collapsible task lists + drag ──
    const expanded     = reactive({})
    const projectTasks = reactive({})
    const loadingTasks = reactive({})

    async function loadProjectTasks(pid) {
      loadingTasks[pid] = true
      try { projectTasks[pid] = (await api.getTasks({ project_id: pid })).data || [] }
      catch { projectTasks[pid] = [] }
      finally { loadingTasks[pid] = false }
    }
    function toggle(p) {
      expanded[p.id] = !expanded[p.id]
      if (expanded[p.id] && projectTasks[p.id] === undefined) loadProjectTasks(p.id)
    }

    async function onProjectReorder() {
      try { await api.reorderProjects(projects.value.map(p => p.id)) } catch {}
    }

    function adjustCount(pid, delta, task) {
      if (task && task.status === 'done') return
      const p = projects.value.find(p => p.id === pid)
      if (p) p.task_count = Math.max(0, (p.task_count || 0) + delta)
    }

    async function onTaskChange(evt, pid) {
      try {
        if (evt.added) {
          const tk = evt.added.element
          tk.project_id = pid
          await api.updateTask(tk.id, { project_id: pid })
          await api.reorderTasks((projectTasks[pid] || []).map(t => t.id))
          adjustCount(pid, +1, tk)
        } else if (evt.moved) {
          await api.reorderTasks((projectTasks[pid] || []).map(t => t.id))
        } else if (evt.removed) {
          await api.reorderTasks((projectTasks[pid] || []).map(t => t.id))
          adjustCount(pid, -1, evt.removed.element)
        }
      } catch {}
    }

    function openForm(proj) {
      editing.value = proj
      Object.assign(pForm, {
        name:         proj?.name         || '',
        description:  proj?.description  || '',
        color:        proj?.color        || '#76B900',
        visibility:   proj?.visibility   || 'shared',
        pricing_type: proj?.pricing_type || 'none',
        price_amount: (proj?.price_amount ?? '') === null ? '' : (proj?.price_amount ?? ''),
        currency:     proj?.currency     || 'ILS',
      })
      pError.value  = ''
      memberToAdd.value = ''
      members.value = []
      clientToAdd.value = ''
      projectClients.value = []
      showForm.value = true
      loadAllClients()
      if (proj) { loadMembers(); loadUsers(); loadProjectClients() }
      else loadUsers()
    }

    async function submitProject() {
      pError.value  = ''
      pSaving.value = true
      try {
        const data = {
          name: pForm.name, description: pForm.description || null, color: pForm.color, visibility: pForm.visibility,
          pricing_type: pForm.pricing_type,
          price_amount: pForm.pricing_type === 'none' || pForm.price_amount === '' ? null : Number(pForm.price_amount),
          currency: pForm.currency || 'ILS',
        }
        if (editing.value) {
          await api.updateProject(editing.value.id, data)
        } else {
          await api.createProject(data)
        }
        showForm.value = false
        await load()
      } catch (e) {
        pError.value = e.message || t('taskForm.saveFailed')
      } finally {
        pSaving.value = false
      }
    }

    async function archiveProject(proj) {
      if (!confirm(t('projects.archiveConfirm', { name: proj.name }))) return
      try { await api.archiveProject(proj.id); projects.value = projects.value.filter(p => p.id !== proj.id) } catch {}
    }

    function realtimeRefresh() {
      load()
      Object.keys(expanded).forEach(pid => { if (expanded[pid]) loadProjectTasks(Number(pid)) })
    }
    let offTask = () => {}, offProj = () => {}
    onMounted(() => {
      load()
      const r = debounce(realtimeRefresh, 400)
      offTask = onRealtime('task.changed', r)
      offProj = onRealtime('project.changed', r)
    })
    onUnmounted(() => { offTask(); offProj() })

    return {
      t, projects, loading, showForm, editing, pForm, pSaving, pError,
      openForm, submitProject, archiveProject, tasksLink,
      members, addableUsers, memberToAdd, showMembers, addMember, removeMember,
      canManage, projectClients, addableClients, clientToAdd, addClient, removeClient,
      dateFrom, dateTo, resetFilters,
      expanded, projectTasks, loadingTasks, toggle, onProjectReorder, onTaskChange,
    }
  },
}
</script>

<style scoped>
.proj-filters {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 20px;
  padding: 12px 16px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius);
}
.proj-filter-label { font-size: 12px; color: var(--muted); }
.filter-date { width: 150px; padding: 6px 10px; font-size: 12px; }

.drag-hint { font-size: 11px; color: var(--faint); margin-bottom: 10px; }
.proj-list { display: flex; flex-direction: column; gap: 10px; }
.project-card {
  background: var(--surface);
  border: 1.5px solid var(--card-brd);
  border-radius: var(--radius);
  overflow: hidden;
  display: flex;
  transition: border-color .15s;
}
.project-card:hover { border-color: var(--border-2); }
.project-color { width: 5px; flex-shrink: 0; }
.project-body  { flex: 1; padding: 12px 16px; min-width: 0; }

.project-head {
  display: flex;
  align-items: center;
  gap: 10px;
}
.spacer { flex: 1; }
.proj-grip { cursor: grab; color: var(--faint); font-size: 14px; line-height: 1; user-select: none; }
.proj-grip:active { cursor: grabbing; }
.proj-toggle { border: none; background: none; cursor: pointer; color: var(--muted); padding: 2px; display: flex; }
.proj-toggle svg { transition: transform .15s; }
[dir="rtl"] .proj-toggle svg { transform: scaleX(-1); }
.proj-toggle svg.open { transform: rotate(90deg); }
[dir="rtl"] .proj-toggle svg.open { transform: scaleX(-1) rotate(-90deg); }
.project-name { font-size: 15px; font-weight: 700; cursor: pointer; }
.project-name:hover { color: var(--accent-ink); }
.vis-badge {
  font-size: 11px;
  font-weight: 600;
  padding: 3px 9px;
  border-radius: 5px;
  white-space: nowrap;
  flex-shrink: 0;
}
.vis-shared  { background: color-mix(in srgb,var(--success) 11%,transparent); color: var(--success); border: 1px solid color-mix(in srgb,var(--success) 34%,transparent); }
.vis-private { background: color-mix(in srgb,var(--warning) 11%,transparent); color: var(--warning); border: 1px solid color-mix(in srgb,var(--warning) 34%,transparent); }

.project-desc { font-size: 12px; color: var(--muted); margin: 8px 0 0; padding-inline-start: 24px; }
.task-count { font-size: 12px; color: var(--muted); white-space: nowrap; }
/* Action cluster sits right next to the title — bold, accent-colored in both themes. */
.project-actions { display: flex; align-items: center; gap: 5px; flex-shrink: 0; margin-inline-start: 4px; }
.pa-view {
  font-weight: 700; color: var(--accent-ink);
  background: var(--accent-wash);
  border: 1px solid color-mix(in srgb, var(--accent-ink) 28%, transparent);
}
.pa-view:hover { color: var(--on-accent); background: var(--accent); border-color: var(--accent); }
.pa-icon {
  color: var(--accent-ink);
  background: var(--accent-wash);
  border: 1px solid color-mix(in srgb, var(--accent-ink) 28%, transparent);
}
.pa-icon svg { stroke-width: 2.4; }
.pa-icon:hover { color: var(--on-accent); background: var(--accent); border-color: var(--accent); }
.danger-icon {
  color: var(--danger);
  background: color-mix(in srgb,var(--danger) 10%,transparent);
  border-color: color-mix(in srgb,var(--danger) 26%,transparent);
}
.danger-icon:hover { color: #fff !important; background: var(--danger) !important; border-color: var(--danger); }

/* Collapsible task list under a project */
.ptasks { margin-top: 12px; padding-top: 10px; border-top: 1px solid var(--border); padding-inline-start: 24px; }
.ptask-list { display: flex; flex-direction: column; gap: 4px; min-height: 12px; }
.ptask {
  display: flex; align-items: center; gap: 10px;
  background: var(--surface-2);
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  padding: 7px 10px;
  cursor: grab;
}
.ptask:active { cursor: grabbing; }
.ptask-grip { color: var(--faint); font-size: 12px; line-height: 1; user-select: none; }
.ptask-title { flex: 1; font-size: 13px; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ptask-title.done { text-decoration: line-through; color: var(--muted); }
.ptask-empty { font-size: 12px; color: var(--faint); padding: 4px 2px; }
.sortable-ghost { opacity: .4; }
.sortable-chosen { border-color: var(--accent); }

.modal-backdrop {
  position: fixed; inset: 0;
  background: rgba(0,0,0,.6);
  display: flex; align-items: center; justify-content: center;
  z-index: 1000; padding: 20px;
}
.modal {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 12px;
  width: 100%; max-width: 480px;
  padding: 28px;
  box-shadow: var(--shadow);
}
.modal-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 20px; }
.modal-header h2 { font-size: 18px; font-weight: 700; }
.modal-close { color: var(--muted); }

.form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }

.color-picker { display: flex; align-items: center; gap: 8px; }
.color-input  { width: 36px; height: 36px; border: none; background: none; cursor: pointer; padding: 0; }
.color-preview { width: 20px; height: 20px; border-radius: 50%; flex-shrink: 0; }

.form-error {
  color: var(--danger); font-size: 13px; margin-bottom: 12px;
  padding: 8px 12px;
  background: rgba(239,68,68,.1);
  border-radius: 6px;
}

.members-block { border-top: 1px solid var(--border); padding-top: 14px; }
.members-hint  { font-size: 11px; color: var(--muted); margin: -2px 0 10px; }
.members-list  { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 10px; }
.member-chip {
  display: inline-flex; align-items: center; gap: 6px;
  background: var(--badge-bg); border: 1px solid var(--border);
  border-radius: 20px; padding: 3px 6px 3px 10px; font-size: 12px;
}
.member-remove {
  border: none; background: none; cursor: pointer; color: var(--muted);
  font-size: 15px; line-height: 1; padding: 0 2px;
}
.member-remove:hover { color: var(--danger); }
.members-empty { font-size: 12px; color: var(--muted); }
.member-add { display: flex; gap: 8px; }
.member-add .form-control { font-size: 12px; padding: 6px 10px; }
.pricing-block { border-top: 1px solid var(--border); padding-top: 14px; }
.pricing-amount { display: flex; gap: 8px; margin-top: 8px; }
.currency-input { max-width: 84px; flex-shrink: 0; }
.modal-actions {
  display: flex; justify-content: flex-end; gap: 10px;
  margin-top: 20px; padding-top: 16px; border-top: 1px solid var(--border);
}

/* Mobile: header wraps so the project name has room; actions drop to their own line */
@media (max-width: 768px) {
  .project-head { flex-wrap: wrap; gap: 8px; }
  .spacer { display: none; }
  .project-name { flex: 1 1 auto; min-width: 0; font-size: 14px; white-space: normal; }
  .task-count { order: 50; }
  .project-actions { order: 99; flex-basis: 100%; justify-content: flex-start; }
  .ptask-title { white-space: normal; }
}
</style>
